import React, { useEffect, useState } from "react";
import { Button, Card, Form, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import api from "../../../services/api";

const blank = {
  name: "",
  city: "",
  googleMapLink: "",
  phoneNumber: "",
  description: "",
  displayOrder: 0,
  active: true,
};

export default function RefreshmentRoomsManager() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(blank);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/admin/refreshment-rooms");
      setRows(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const patch = (field, value) => {
    setEditing((prev) => ({ ...prev, [field]: value }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = normalize(editing);
      const { data } = payload.id
        ? await api.put(`/api/admin/refreshment-rooms/${payload.id}`, payload)
        : await api.post("/api/admin/refreshment-rooms", payload);
      setRows((prev) =>
        payload.id ? prev.map((row) => (row.id === data.id ? data : row)) : [...prev, data],
      );
      setEditing(blank);
      toast.success("Refreshment room saved");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not save refreshment room");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this refreshment room?")) return;
    await api.delete(`/api/admin/refreshment-rooms/${id}`);
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  return (
    <Card className="shadow-sm">
      <Card.Header>
        <div className="fw-semibold">Refreshment Rooms Master</div>
        <div className="text-muted small">Create reusable refreshment rooms, then tick them for each pickup in a trip's Dynamic Pricing.</div>
      </Card.Header>
      <Card.Body>
        <Card className="mb-3">
          <Card.Body>
            <div className="fw-semibold mb-3">{editing.id ? "Edit Refreshment Room" : "New Refreshment Room"}</div>
            <div className="row g-3">
              <div className="col-md-4">
                <Form.Label>Name</Form.Label>
                <Form.Control value={editing.name || ""} onChange={(e) => patch("name", e.target.value)} />
              </div>
              <div className="col-md-3">
                <Form.Label>City</Form.Label>
                <Form.Control value={editing.city || ""} onChange={(e) => patch("city", e.target.value)} />
              </div>
              <div className="col-md-5">
                <Form.Label>Maps Link</Form.Label>
                <Form.Control value={editing.googleMapLink || ""} onChange={(e) => patch("googleMapLink", e.target.value)} />
              </div>
              <div className="col-md-3">
                <Form.Label>Phone Number</Form.Label>
                <Form.Control type="tel" value={editing.phoneNumber || ""} onChange={(e) => patch("phoneNumber", e.target.value)} placeholder="Shown in the driver message" />
              </div>
              <div className="col-md-5">
                <Form.Label>Description</Form.Label>
                <Form.Control as="textarea" rows={2} value={editing.description || ""} onChange={(e) => patch("description", e.target.value)} />
              </div>
              <div className="col-md-2">
                <Form.Label>Order</Form.Label>
                <Form.Control type="number" value={editing.displayOrder || 0} onChange={(e) => patch("displayOrder", Number(e.target.value || 0))} />
              </div>
              <div className="col-md-2 d-flex align-items-end">
                <Form.Check type="switch" label="Active" checked={editing.active !== false} onChange={(e) => patch("active", e.target.checked)} />
              </div>
            </div>
            <div className="d-flex justify-content-end gap-2 mt-3">
              <Button variant="outline-secondary" onClick={() => setEditing(blank)}>Clear</Button>
              <Button disabled={saving || !editing.name || !editing.city} onClick={save}>{saving ? "Saving..." : "Save Room"}</Button>
            </div>
          </Card.Body>
        </Card>

        {loading ? (
          <div className="py-5 text-center"><Spinner /></div>
        ) : (
          <Table responsive hover size="sm" className="align-middle">
            <thead>
              <tr><th style={{ width: 44 }}>#</th>
                <th>Name</th>
                <th>City</th>
                <th>Phone</th>
                <th>Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row.id}><td className="small fw-semibold">{index + 1}</td>
                  <td>
                    <div className="fw-semibold">{row.name}</div>
                    <div className="small text-muted">{row.description}</div>
                  </td>
                  <td>{row.city || "-"}</td>
                  <td>{row.phoneNumber || "-"}</td>
                  <td>{row.active ? "Active" : "Inactive"}</td>
                  <td className="text-end">
                    {row.googleMapLink ? <Button as="a" href={row.googleMapLink} target="_blank" rel="noreferrer" size="sm" variant="outline-primary" className="me-2">Map</Button> : null}
                    <Button size="sm" variant="outline-secondary" className="me-2" onClick={() => setEditing(row)}>Edit</Button>
                    <Button size="sm" variant="outline-danger" onClick={() => remove(row.id)}>Delete</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
}

function normalize(row) {
  return {
    ...row,
    name: String(row.name || "").trim(),
    city: String(row.city || "").trim(),
    googleMapLink: String(row.googleMapLink || "").trim(),
    phoneNumber: String(row.phoneNumber || "").trim(),
    description: String(row.description || "").trim(),
    displayOrder: Number(row.displayOrder || 0),
    active: row.active !== false,
  };
}
